import { useMemo } from 'react';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';

import { useSupporterVoicesFeed } from '../../hooks/useSupporterVoicesFeed';

import type { SupporterVoicesFeedContextValueType } from './types';

/**
 * Single instance of the supporter-voices feed state. Calling `useSupporterVoicesFeed` here (and
 * only here) means there is exactly one `useSupporterVideos` state instance per render of the
 * supporters section, so `removeVideoFromState` stays consistent across both the page layout
 * decision (Main.tsx) and the feed render (SupporterVoicesFeed).
 */
export function useSupporterVoicesFeedContextValue(): SupporterVoicesFeedContextValueType {
	const {
		data: {
			featureConfigs: { supporterVoicesMix },
		},
	} = usePetitionDetailsPageData();

	const { data, actions } = useSupporterVoicesFeed();

	// The mixed feed replaces the legacy sections only when the FCM is set AND there are enough
	// items to fill it. Both the page layout wrapper (Main.tsx) and the section content read this
	// so their decisions can't diverge.
	const showSupporterVoicesFeed = Boolean(supporterVoicesMix) && data.showFeed;

	return useMemo(
		() => ({
			data: { ...data, showSupporterVoicesFeed },
			actions,
		}),
		[data, showSupporterVoicesFeed, actions],
	);
}
